import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import type { CartItem, EmbedToWidgetMessage } from "@babackapp/return-widget-components/types/shop-now";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadCart, saveCart } from "../lib/shop-now-cart";
import { postToParent } from "../lib/shop-now-embed";
import { useWidgetParams } from "./use-widget-params";

type UseShopNowResolveResult = {
  items: CartItem[];
  currency: string;
  cartTotal: number;
  handleAddMore: () => void;
  handleConfirm: () => void;
};

export function useShopNowResolve(): UseShopNowResolveResult {
  const navigate = useNavigate();
  const { orderId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);

  const cached = loadCart(orderId);
  const [items, setItems] = useState<CartItem[]>(cached?.items ?? []);
  const [currency, setCurrency] = useState(cached?.currency ?? "EUR");

  useEffect(() => {
    postToParent({ type: "WIDGET_SHOP_NOW_READY" });
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToWidgetMessage;
      if (msg?.type === "SHOP_NOW_RESUME") {
        setItems(msg.items);
        setCurrency("EUR");
        saveCart(orderId, msg.items, "EUR");
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [orderId]);

  const cartTotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleAddMore = () => postToParent({ type: "SHOP_NOW_START" });

  const handleConfirm = () => {
    const remaining = financialSummary.exchangeTotal - cartTotal;
    navigate(remaining > 0 ? "../shop-now-remaining-credit" : "../complete");
  };

  return { items, currency, cartTotal, handleAddMore, handleConfirm };
}
